import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IcommerceUser, IdataArrayResponse,
  IdeleteOneResponse,
  IecommerceOrder,
  IfilterProps,
  IunreliableBuyerHistory
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../../commerce-client';

/**
 * Type alias for unreliable buyer history properties with specific generic types
 * @template U - Type for user (default: IcommerceUser)
 * @template T - Type for order (default: IecommerceOrder<number, IcommerceUser>)
 */
type TunreliableBuyerHistoryProps = IunreliableBuyerHistory<IcommerceUser, IecommerceOrder>;

/**
 * Represents an unreliable buyer history entry for a specific order.
 * Extends DatabaseAuto to inherit common database operations.
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link UnreliableBuyerHistory} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const unreliableBuyerHistory = await UnreliableBuyerHistory.getAll();
 *
 * const unreliableBuyerHistory = await UnreliableBuyerHistory.filterAll({
 *
 * });
 *
 * const unreliableBuyerHistory = await UnreliableBuyerHistory.bulkDelete({
 *
 * });
 *
 * const unreliableBuyerHistory = await UnreliableBuyerHistory.getOne('resourceId');
 *
 * const unreliableBuyerHistory = await UnreliableBuyerHistory.remove();
 *
 * ```
 */
export class UnreliableBuyerHistory
  extends DatabaseAuto {
  /** Base URL for the unreliable buyer history API endpoint */
  private static readonly baseUrl = '/un-reliable-buyer-history';

  /** User associated with the history entry */
  readonly buyer: TunreliableBuyerHistoryProps['buyer'];

  /** Reason for the history entry */
  readonly reason: TunreliableBuyerHistoryProps['reason'];

  /** Action taken as a result of the history entry */
  readonly action: TunreliableBuyerHistoryProps['action'];

  /** Order associated with the history entry */
  readonly ecommerceOrder: TunreliableBuyerHistoryProps['ecommerceOrder'];

  /**
   * Constructs an instance of `unreliableBuyerHistory`.
   *
   * @param {TunreliableBuyerHistoryProps} data - The unreliableBuyerHistory data used to initialize the instance.
   * @throws {Error} Throws an error if no data is provided.
   */
  constructor(data: TunreliableBuyerHistoryProps) {
    super(data);

    this.buyer = data.buyer;
    this.reason = data.reason;
    this.action = data.action;
    this.ecommerceOrder = data.ecommerceOrder;
  }

  /**
   * Retrieves all unreliableBuyerHistorys with pagination.
   *
   * @param {number} offset - The starting point for the retrieval.
   * @param {number} limit - The number of records to retrieve.
   * @returns {Promise<{ count: number; dataArr: UnreliableBuyerHistory[] }>}
   * A promise resolving to an object containing count and data array.
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TunreliableBuyerHistoryProps>>(`${UnreliableBuyerHistory.baseUrl}?${query}`);
    const unreliableBuyerHistorys = await lastValueFrom(observer$);

    return {
      count: unreliableBuyerHistorys.count,
      dataArr: unreliableBuyerHistorys.data.map((val) => new UnreliableBuyerHistory(val))
    };
  }

  /**
   * Filters unreliableBuyerHistorys based on the provided criteria.
   *
   * @param {IfilterProps} filter - The filter criteria.
   * @returns {Promise<{ count: number; dataArr: UnreliableBuyerHistory[] }>}
   * A promise resolving to an object containing count and data array.
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TunreliableBuyerHistoryProps>>(
        `${UnreliableBuyerHistory.baseUrl}/filter`,
        filter
      );
    const unreliableBuyerHistorys = await lastValueFrom(observer$);

    return {
      count: unreliableBuyerHistorys.count,
      dataArr: unreliableBuyerHistorys.data.map((val) => new UnreliableBuyerHistory(val))
    };
  }

  /**
   * Retrieves a single unreliableBuyerHistory by resource ID.
   *
   * @param {string} resourceId - The ID of the unreliableBuyerHistory to retrieve.
   * @returns {Promise<UnreliableBuyerHistory>} The retrieved `UnreliableBuyerHistory` instance.
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TunreliableBuyerHistoryProps>(`${UnreliableBuyerHistory.baseUrl}/${resourceId}`);
    const unreliableBuyerHistory = await lastValueFrom(observer$);

    return new UnreliableBuyerHistory(unreliableBuyerHistory);
  }

  /**
   * Removes multiple unreliable buyer history entries based on the provided criteria.
   *
   * @param {IbulkDelete} payload - The criteria for bulk deletion
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk delete operation
   * @throws {Error} When the deletion fails or payload is invalid
   *
   * @example
   * const result = await UnreliableBuyerHistory.bulkDelete({
   *   ids: ['history1', 'history2'],
   *   hardDelete: true
   * });
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(
        `${UnreliableBuyerHistory.baseUrl}/bulk-delete`,
        payload
      );

    return lastValueFrom(observer$);
  }

  /**
   * Removes the current unreliable buyer history entry from the system.
   *
   * @returns {Promise<IdeleteOneResponse | null>} The result of the delete operation
   * @throws {Error} When the deletion fails
   *
   * @example
   * const historyEntry = await UnreliableBuyerHistory.getOne('history123');
   * const result = await historyEntry.remove();
   * if (result?.success) {
   *   console.log('History entry deleted successfully');
   * }
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${UnreliableBuyerHistory.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
